import React, { useEffect, useState } from "react";

import { Grid, TextField, Button, Dialog, DialogActions, DialogContent, DialogTitle, CircularProgress } from "@material-ui/core";
import { SelectorFecha, SelectorFechaHora } from "componentes/ui/fechas";

/**
 *
 * @typedef {Object} InputElement
 * @property {string} id Identificador de elemento
 * @property {string} label Texto de elemento
 * @property {'text'|'number'|'date'|'datetime'} inputType Tipo de elemento
 */

/**
 * Especifica la función a ser ejecutada al hacer click en botón
 * @callback OnClick
 * @returns {void}
 */

/**
 * Especifica la función a ser ejecutada al finalizar
 * @callback OnOk
 * @param {{ id: any; value: any }[]} results
 * @returns {Promise<void>}
 */

/**
 * 
 * @typedef {Object} Props
 * @property {string} title Titulo de dialogo
 * @property {string} message Mensaje de cuerpo de dialogo
 * @property {InputElement[]} fields Campos de entrada
 * @property {{ btnCancel: '', btnOk: '' }} labels Etiquetas
 * @property {bool} isOpen Si dialogo está abierto
 * @property {bool} isLoading Si dialogo está cargando
 * @property {OnOk} onOk Evento al presionar botón ok 
 * @property {OnClick} onCancel Evento al presionar botón cancelar
 */

/**
 * Dialogo de entrada
 * @param {Props} props
 * @param {ChildrenFn} props.children
 * @returns 
 */
export const InputDialog = ({ 
    title = '', 
    message, 
    fields = [],
    labels = { btnCancel: '', btnOk: '' }, 
    isOpen = true, 
    isLoading = false, 
    onOk = (result) => {}, 
    onCancel = () => {}, 
}) => {
    const [inputTexts, setInputTexts] = useState([]);

    useEffect(() => {
        setInputTexts(fields.map(x => ({ 
            id: x.id, 
            value: x.inputType === 'number' 
            ? 0 
            : x.inputType === 'date' 
                ? new Date() 
                : x.inputType === 'datetime' 
                    ? new Date() 
                    : '',
            })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


    return (
        <Dialog
            maxWidth="xs"
            aria-labelledby="confirmation-dialog-title"
            open={isOpen}
            onClose={(event, reason) => {
                if (reason === "backdropClick") {
                    return false;
                }

                if (reason === "escapeKeyDown") {
                    return false;
                }
            }}
        >
            <DialogTitle id="confirmation-dialog-title">{title}</DialogTitle>
            <DialogContent dividers>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={12}>
                        {message}
                    </Grid>
                    {fields.map((x, i) => (
                        <Grid key={`field-${x.id}`} item xs={12} md={12}>
                            {x.inputType === 'text' && (
                                <TextField
                                    name={x.id}
                                    label={x.label}
                                    variant="outlined"
                                    fullWidth
                                    size="small"
                                    multiline
                                    minRows={2}
                                    maxRows={3}
                                    required
                                    value={inputTexts[i]?.value}
                                    onChange={(e) => setInputTexts(prev => [
                                        ...prev.filter(y => y.id !== x.id), 
                                        { id: x.id, value: e.target.value },
                                    ])}
                                />
                            )}
                            {x.inputType === 'number' && (
                                <TextField
                                    name={x.id}
                                    label={x.label}
                                    variant="outlined"
                                    fullWidth
                                    size="small"
                                    type="number"
                                    required
                                    value={inputTexts[i]?.value}
                                    onChange={(e) => setInputTexts(prev => [
                                        ...prev.filter(y => y.id !== x.id), 
                                        { id: x.id, value: +e.target.value },
                                    ])}
                                />
                            )}
                            {x.inputType === 'date' && (
                                <SelectorFecha
                                    name={x.id}
                                    label={x.label}
                                    value={inputTexts[i]?.value}
                                    clearable={false}
                                    onChange={(d) => setInputTexts(prev => [
                                        ...prev.filter(y => y.id !== x.id), 
                                        { id: x.id, value: d },
                                    ])}
                                />
                            )}
                            {x.inputType === 'datetime' && (
                                <SelectorFechaHora
                                    name={x.id}
                                    label={x.label}
                                    value={inputTexts[i]?.value}
                                    clearable={false}
                                    format='dd/MM/yyyy HH:mm'
                                    ampm={false}
                                    minutesStep={60}
                                    onChange={(d) => setInputTexts(prev => [
                                        ...prev.filter(y => y.id !== x.id), 
                                        { id: x.id, value: d },
                                    ])}
                                />
                            )}
                        </Grid>    
                    ))}
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button autoFocus onClick={onCancel} color="primary" disabled={isLoading}>
                    {labels.btnCancel}
                </Button>
                <Button onClick={() => onOk(inputTexts)} color="secondary" disabled={isLoading}>
                    {isLoading && <CircularProgress size={20} style={{ marginRight: 5 }} />} {labels.btnOk}
                </Button>
            </DialogActions>
        </Dialog>
    );
};